import { FC, useEffect, useState } from "react";
import { IReturnToContractorCreateDTO, IReturnToContractorDTO, IReturnToContractorItemDTO, IReturnToContractorUpdateDTO } from "../../../../libs/coreapi-dto/documents/actReturnToContractor";
import { ActReturnToContractorDataProvider } from "../../../../Services/DataProviders/ActReturnToContractorDataProvider";
import { useAppContext } from "../../../../system/providers/appContextProvider";
import { useTabsContext } from "../../../../system/providers/tabsProvider";
import { ActReturnToContractorCreate } from "../forms/ActReturnToContractorCreate";
import { v4 as uuidv4 } from "uuid";
import { IDocumentCreatorProps } from "../../../../@types/documents";
import useOperativeReserve from "../../../../components/lotSelector/useOperativeReserve";
import { IdTableVariant } from "../../../../@types/enumsGlobal";
import { getActReturnToContractorItemUpdateDTO } from "../../../../libs/core-api-requests/customRequest/ActReturnToContractorItemUpdateRequest";
import { DocumentOperationSpinner } from "../../../../components/DocumentOperationSpinner/DocumentOperationSpinner";
import { ca } from "date-fns/locale";

interface ICreateDocument {
    callback: (entity: IReturnToContractorUpdateDTO, withClose: boolean, view: GridStateType) => void;
}

interface IEditDocument {
    callback: (id: string, entity: IReturnToContractorUpdateDTO, withClose: boolean) => void;
    entity: IReturnToContractorDTO;
}

interface ICopyDocument {
    callback: (entity: IReturnToContractorUpdateDTO, withClose: boolean) => void;
    entity: IReturnToContractorDTO;
}

export const ActReturnToContractorCreatePluginView: FC<IDocumentCreatorProps<IReturnToContractorCreateDTO>> = (props) => {
    const tabsContext = useTabsContext();
    const appContext = useAppContext();

    const artcDP = new ActReturnToContractorDataProvider(appContext.coreApiService);

    const [viewState, setViewState] = useState<GridStateType>(props.variant);
    const [id, setId] = useState<string | undefined>(props.idGlobal ?? uuidv4());
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const operativeReserve = useOperativeReserve();
    const [createDocument, setCreateDocument] = useState<ICreateDocument | null>(null);
    const [editDocument, setEditDocument] = useState<IEditDocument | null>(null);
    const [copyDocument, setCopyDocument] = useState<ICopyDocument | null>(null);

    useEffect(() => {
        const initializeComponent = async () =>{
            setIsLoading (true)
            
            if (viewState === "create") {
                const createCallback = (entity: IReturnToContractorUpdateDTO, withClose: boolean, view: GridStateType) => {
                    const tabToClose = tabsContext.currentTab;
                    setIsSubmitting(true);
                    artcDP.create(entity, (id) => {
                        setId(id);
                        setViewState(view);
                        props.createdCallback?.();
                        withClose && tabToClose && tabsContext.close(tabToClose);
                        setIsSubmitting(false);
                    }, ()=>{}, (e)=>{
                        setIsSubmitting(false);
                    });
                };
                setCreateDocument({ callback: createCallback });
                setIsLoading(false);
            }
    
            else if (viewState === "edit") {
                const editCallback = (id: string, entity: IReturnToContractorUpdateDTO, withClose: boolean) => {
                    const tabToClose = tabsContext.currentTab;
                    setIsSubmitting(true);
                    artcDP.updateExt(
                        id as string,
                        entity,
                        () => {
                            setIsSubmitting(false);
                            props.createdCallback?.(entity);
                            if (withClose && tabToClose) {
                                tabsContext.close(tabToClose);
                            }
                        },
                        (e) => {
                            setIsSubmitting(false);
                            throw new Error(e.title);
                        }
                    );
                };
                try {
                    await new Promise((resolve, reject) => {
                        artcDP.getById(id as string, (entity) => {
                            getActReturnToContractorItemUpdateDTO(entity.idGlobal as string, e => {
                                setEditDocument({
                                    callback: editCallback,
                                    entity: {
                                        ...entity,
                                        items: e
                                    },
                                });
                                resolve(null)
                            });
                        });
                    });
                } catch (error) {
                    console.error("Error fetching document for edit:", error);
                } finally {
                    setIsLoading(false);
                }
            }
    
            if (viewState === "copy") {
                const copyCallback = (entity: IReturnToContractorUpdateDTO, withClose: boolean) => {
                    const tabToClose = tabsContext.currentTab;
                    setIsSubmitting(true);
                    artcDP.create(entity, (id) => {
                        setId(id);
                        setViewState("edit");
                        props.createdCallback?.();
                        withClose && tabToClose && tabsContext.close(tabToClose);
                        setIsSubmitting(false);
                    }, ()=>{}, (e)=>{
                        setIsSubmitting(false);
                    });
                };
                try {
                    await new Promise<void>((resolve) => {
                        artcDP.getById(id as string, async (entity) => {
                            getActReturnToContractorItemUpdateDTO(entity.idGlobal as string, e => {
                                const idGlobalNew = uuidv4();
                                const itemsCopy: IReturnToContractorItemDTO[] = e.map((item: IReturnToContractorItemDTO) => ({
                                    ...item,
                                    idGlobal: uuidv4(),
                                    kizs: [],
                                    kizBoxes: [],
                                    kizOsuDtos: [],
                                    kizOsuDto: undefined,
                                }));
                                const documentCopy: IReturnToContractorDTO = {
                                    ...entity,
                                    idGlobal: idGlobalNew,
                                    mnemocode: '',
                                    items: itemsCopy,
                                };
                                setCopyDocument({
                                    callback: copyCallback,
                                    entity: documentCopy,
                                });
                                resolve();
                            });
                        });
                    });
                } catch (error) {
                    console.error('Error loading document for copy:', error);
                } finally {
                    setIsLoading(false);
                }
            }
        }
        initializeComponent()        
    }, [viewState]);

    const deleteAllReserve = (id: string, onSuccess: () => void) => {
        operativeReserve.deleteAll(
            {
                idTable: IdTableVariant.ActReturnToContractor,
                idDocument: id,
            },
            onSuccess
        );
    };

    if (isLoading) {
            return (
                <div >
                    <DocumentOperationSpinner xl data-testid="invoice-creator-loading" />                
                </div>
            );
        }

    return (
        <>
            {viewState === "create" ? (
                createDocument && (
                    <ActReturnToContractorCreate
                        isSubmitting={isSubmitting}
                        variant="create"
                        idGlobal={id}
                        save={(entity) => createDocument.callback(entity, false, "edit")}
                        ok={(entity) => createDocument.callback(entity, true, "create")}
                        cancel={() => deleteAllReserve(id as string, tabsContext.closeCurrent)}
                        permission={props.permission}
                    />
                )
            ) : viewState === "edit" ? (
                editDocument && (
                    <ActReturnToContractorCreate
                        isSubmitting={isSubmitting}
                        idGlobal={editDocument.entity.idGlobal}
                        variant="edit"
                        document={editDocument.entity}
                        save={(value) => editDocument.callback(editDocument.entity.idGlobal as string, value, false)}
                        ok={(value) => editDocument.callback(editDocument.entity.idGlobal as string, value, true)}
                        cancel={() => deleteAllReserve(id as string, tabsContext.closeCurrent)}
                        permission={props.permission}
                    />
                )
            ) : viewState === "copy" ? (
                copyDocument && (
                    <ActReturnToContractorCreate
                        isSubmitting={isSubmitting}
                        idGlobal={copyDocument.entity.idGlobal}
                        variant="copy"
                        document={copyDocument.entity}
                        save={(value) => copyDocument.callback(value, false)}
                        ok={(value) => copyDocument.callback(value, true)}
                        cancel={() => deleteAllReserve(id as string, tabsContext.closeCurrent)}
                        permission={props.permission}
                    />
                )
            ) : (
                <></>
            )}
        </>
    );
};
